import {
  BinaryFileAssetTask,
  Color3,
  DynamicTexture,
  PBRMaterial,
  Scene,
  StandardMaterial,
  TextFileAssetTask,
  Texture,
} from '@babylonjs/core';
import { Asset, AssetType } from './Asset';
import { ImageUtils } from './ImageUtils';

/**
 * Texture atlas for js
 *
 * Loads atlas data from json and expects json like:
 * { "frames":{
 *     "frame_001": {"frame": {"x":0, "y":32,"w":22,"h":18} },
 *     "frame_002": {"frame": {"x":53,"y":0, "w":22,"h":21} }
 * }}
 *
 * @class TextureAtlas
 */
export class TextureAtlas {
  scene: Scene;
  baseTexture: Texture;
  mormalMapTexture: Texture;
  data = null;
  frames = [];
  image: HTMLImageElement;

  public assets: Asset[];

  constructor(
    public name: string,
    public rootURL: string,
    public imgURL: string,
    public jsonURL: string,
    public normalMapImgURL?: string,
    public noMipmap: boolean = false,
    public sampling: number = Texture.TRILINEAR_SAMPLINGMODE
  ) {
    this.assets = [
      new Asset(name + 'Image', AssetType.BINARY, rootURL, imgURL),
      new Asset(name + 'Json', AssetType.TEXT, rootURL, jsonURL),
    ];
    if (normalMapImgURL) {
      this.assets.push(new Asset(name + 'NormalMap', AssetType.BINARY, rootURL, imgURL));
    }
  }

  /**
   * Loads atlas data from json and set internal frames
   * Expects json like:
   * { "frames":{
   *     "frame_001": {"frame": {"x":0, "y":32,"w":22,"h":18} },
   *     "frame_002": {"frame": {"x":53,"y":0, "w":22,"h":21} }
   * }}
   *
   * @memberof TextureAtlas
   */
  async create(scene: Scene): Promise<TextureAtlas> {
    this.scene = scene;

    const imageTask: BinaryFileAssetTask = this.assets[0].getTask();
    const imageBlobUrl = ImageUtils.blobUrlFromArrayBuffer(imageTask.data);

    this.image = new Image();
    this.image.src = imageBlobUrl;
    await this.image.decode();

    // texture loader and event
    this.baseTexture = new Texture(imageBlobUrl, this.scene, this.noMipmap, true, this.sampling);

    // atlas will almost always need alpha
    this.baseTexture.hasAlpha = true;

    if (this.normalMapImgURL) {
      const imageNormalMap: BinaryFileAssetTask = this.assets[2].getTask();
      const normalMapUrl = ImageUtils.blobUrlFromArrayBuffer(imageNormalMap.data);

      this.mormalMapTexture = new Texture(normalMapUrl, this.scene, this.noMipmap, true, this.sampling);
    }

    const jsonTask: TextFileAssetTask = this.assets[1].getTask();
    this.data = JSON.parse(jsonTask.text);
    const list = Object.keys(this.data.frames);
    for (let i = 0; i < list.length; i++) {
      this.frames.push(list[i]);
    }

    return this;
  }

  /**
   * Create a texture with the right uv settings for a given frame
   *
   * @param {(string|number)} frame
   * @returns {Texture}
   */
  createTexture(frame: string | number, dynamic = false): Texture {
    if (!dynamic) {
      const tex = this.baseTexture.clone();
      this.setTextureFrame(tex, frame);
      return tex;
    }

    const frameData = this.getFrameData(frame);

    //Create dynamic texture
    const dynamicTexture = new DynamicTexture(
      'dynamic-texture-' + frame,
      { width: frameData.frame.w, height: frameData.frame.h },
      this.scene
    );
    const textureContext = dynamicTexture.getContext();
    //Add image to dynamic texture
    textureContext.drawImage(
      this.image,
      frameData.frame.x,
      frameData.frame.y,
      frameData.frame.w,
      frameData.frame.h,
      0,
      0,
      frameData.frame.w,
      frameData.frame.h
    );

    dynamicTexture.wrapU = 1;
    dynamicTexture.wrapV = 1;
    dynamicTexture.wrapR = 1;

    dynamicTexture.update();

    return dynamicTexture;
  }

  /**
   * Create a PBR material with a texture from the specified frame
   *
   * @param {string | number} frame - The frame name or index
   * @returns {PBRMaterial}
   */
  createPBRMaterial(frame: string | number): PBRMaterial {
    const material = new PBRMaterial('pbr-frame-' + frame, this.scene);
    const frameData = this.getFrameData(frame);

    // Create an offscreen canvas to extract the frame
    const canvas = document.createElement('canvas');
    canvas.width = frameData.frame.w;
    canvas.height = frameData.frame.h;
    const ctx = canvas.getContext('2d');

    // Draw the frame portion from the atlas image
    ctx.drawImage(
      this.image,
      frameData.frame.x,
      frameData.frame.y,
      frameData.frame.w,
      frameData.frame.h,
      0,
      0,
      frameData.frame.w,
      frameData.frame.h
    );

    // Convert canvas to blob URL and create texture
    const dataUrl = canvas.toDataURL('image/png');
    const texture = new Texture(dataUrl, this.scene, false, true, Texture.TRILINEAR_SAMPLINGMODE);

    // Set the texture as albedo texture
    material.albedoTexture = texture;

    // Set PBR properties for non-metallic, diffuse surface
    material.metallic = 0;
    material.roughness = 1;

    return material;
  }

  /**
   * Create a material using the unframed base texture
   *
   * @param {AtlasMaterialConfig} config
   * @returns {StandardMaterial}
   */
  createMaterial(config: AtlasMaterialConfig): StandardMaterial {
    const material = new StandardMaterial('mat-frame-' + config.frame, this.scene);
    material.backFaceCulling = config.backFaceCulling;
    material.alpha = config.alpha;
    material.diffuseTexture = this.baseTexture;

    if (this.mormalMapTexture && config.useBumpTexture) {
      material.bumpTexture = this.mormalMapTexture;
      material.specularColor = new Color3(0.3, 0.3, 0.3);
      material.emissiveColor = new Color3(0.1, 0.1, 0.1);
    } /* else {
              material.specularColor = new Color3(0, 0, 0);
              material.emissiveColor = new Color3(1, 1, 1);
          } */

    if (config.specularColor) {
      material.specularColor = config.specularColor;
    }

    if (config.emissiveColor) {
      material.emissiveColor = config.emissiveColor;
    }

    material.freeze();
    (<any>material).currentAtlasFrame = config.frame;
    return material;
  }

  getUVCoordinates(frame: string | number): any {
    let frameDat = this.getFrameData(frame);
    return {
      sw: this.data.meta.size.w,
      sh: this.data.meta.size.h,
      x: frameDat.frame.x / this.data.meta.size.w,
      y: frameDat.frame.y / this.data.meta.size.h,
      w: frameDat.frame.w / this.data.meta.size.w,
      h: frameDat.frame.h / this.data.meta.size.h,
    };
  }

  setUVs(frame: string | number, uvs: number[], axis: number, uScale: number, vScale: number) {
    let uvCoords = this.getUVCoordinates(frame);

    if (axis === 2) {
      uvs.push(
        uvCoords.x,
        1 - uvCoords.y - uvCoords.h,
        uvCoords.x + uvCoords.w,
        1 - uvCoords.y - uvCoords.h,
        uvCoords.x + uvCoords.w,
        1 - uvCoords.y,
        uvCoords.x,
        1 - uvCoords.y
      );
    } else {
      uvs.push(
        uvCoords.x + uvCoords.w,
        1 - uvCoords.y - uvCoords.h,
        uvCoords.x + uvCoords.w,
        1 - uvCoords.y,
        uvCoords.x,
        1 - uvCoords.y,
        uvCoords.x,
        1 - uvCoords.y - uvCoords.h
      );
    }
  }

  /**
   * Set a created texture's uv settings to the given frame
   *
   * @param {*} tex
   * @param {(string|number)} frame
   * @memberof Atlas
   */
  setTextureFrame(tex: Texture, frame: string | number) {
    const frameDat = this.getFrameData(frame);
    this.setTextureUVs(tex, frameDat);
  }

  dispose() {
    this.baseTexture.dispose();
    this.data = null;
    this.scene = null;
    this.frames.length = 0;
  }

  /**
   * Interpret string or number frame value, and return frame data from JSON
   *
   * @param {(string|number)} frame
   * @returns
   * @memberof Atlas
   */
  getFrameData(frame: string | number): any {
    let framestr = '';
    if (typeof frame === 'number') {
      framestr = this.frames[frame];
    } else {
      framestr = frame;
    }
    const dat = this.data.frames[framestr];
    if (!dat) {
      throw new Error('babylon-atlas: frame "' + framestr + '" not found in atlas');
    }

    return dat;
  }

  /**
   * For a given frame's x/y/width/height, set the textures UVs to^ display that part of the texture
   *
   * @param {Texture} tex
   * @param {*} frameDat
   * @memberof TextureAtlas
   */
  setTextureUVs(tex: Texture, frameDat: any) {
    const sw = this.data.meta.size.w;
    const sh = this.data.meta.size.h;
    const x = frameDat.frame.x;
    const y = frameDat.frame.y;
    const w = frameDat.frame.w;
    const h = frameDat.frame.h;

    tex.uScale = w / sw;
    tex.vScale = h / sh;
    tex.uOffset = x / sw;
    tex.vOffset = (sh - y - h) / sh;

    tex.wrapU = 1;
    tex.wrapV = 1;
    tex.wrapR = 1;
  }
}

export class AtlasMaterialConfig {
  alpha: number;
  frame: string | number;
  useBumpTexture: boolean;
  backFaceCulling: boolean;
  specularColor: Color3;
  emissiveColor: Color3;

  constructor(
    frame: string | number,
    alpha: number = 1,
    useBumpTexture: boolean = true,
    backFaceCulling: boolean = true,
    specularColor: Color3 = null,
    emissiveColor: Color3 = null
  ) {
    this.frame = frame;
    this.alpha = alpha;
    this.useBumpTexture = useBumpTexture;
    this.backFaceCulling = backFaceCulling;
    this.specularColor = specularColor;
    this.emissiveColor = emissiveColor;
  }
}
